import React, { useEffect, useState, useCallback } from 'react';
import { listAllNews, createNews, updateNews, setNewsArchived } from '../firebase/admin';
import { NewsArticle } from '../components/News';

// Today's date as 'YYYY-MM-DD' for the default on a new article.
function todayISO() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const emptyForm = () => ({ title: '', date: todayISO(), content: '' });
const shortDate = (iso) => iso || '—';

// Admin News manager: list articles on the left, create/edit on the right, with
// a live Markdown preview. Archiving hides an article from the member-facing
// feed without deleting it (restorable from here). Reuses the ua-* / na-*
// styles in Admin.css.
const NewsAdmin = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // `selectedId` is a doc id when editing, 'new' when creating, or null.
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setArticles(await listAllNews());
    } catch (err) {
      setError(err?.message || 'Failed to load news.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = selectedId && selectedId !== 'new'
    ? articles.find((a) => a.id === selectedId) || null
    : null;

  const startNew = () => {
    setSelectedId('new');
    setForm(emptyForm());
    setError('');
  };

  const startEdit = (a) => {
    setSelectedId(a.id);
    setForm({ title: a.title, date: a.date || todayISO(), content: a.content });
    setError('');
  };

  const closeEditor = () => {
    setSelectedId(null);
    setForm(emptyForm());
    setError('');
  };

  const archivedCount = articles.filter((a) => a.archived).length;
  const visible = articles.filter((a) => showArchived || !a.archived);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const canSave = form.title.trim() && form.date && form.content.trim() && !saving;

  const save = async () => {
    setSaving(true);
    setError('');
    const payload = {
      title: form.title.trim(),
      date: form.date,
      content: form.content,
    };
    try {
      if (selectedId === 'new') {
        const created = await createNews(payload);
        setArticles((prev) => [created, ...prev]);
        setSelectedId(created.id);
      } else {
        const updated = await updateNews(selectedId, payload);
        setArticles((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      }
    } catch (err) {
      setError(err?.message || 'Failed to save the article.');
    } finally {
      setSaving(false);
    }
  };

  const toggleArchived = async (a, archived) => {
    setArchiving(true);
    setError('');
    try {
      const updated = await setNewsArchived(a.id, archived);
      setArticles((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
    } catch (err) {
      setError(err?.message || 'Failed to update the article.');
    } finally {
      setArchiving(false);
    }
  };

  return (
    <div className="ua">
      <div className="ua-grid">
        {/* Left: article list */}
        <aside className="ua-list glass">
          <div className="ua-list-head">
            <h2>Articles {!loading && <span className="ua-count">{visible.length}</span>}</h2>
            <button className="ua-refresh" onClick={load} disabled={loading} title="Refresh">
              {loading ? '…' : '↻'}
            </button>
          </div>

          <button className="btn-primary na-new" onClick={startNew}>
            + New article
          </button>

          <label className="ua-filter">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            Show archived
            {archivedCount > 0 && <span className="ua-filter-count">{archivedCount}</span>}
          </label>

          {loading ? (
            <p className="ua-muted">Loading news…</p>
          ) : error && !articles.length ? (
            <p className="ua-error">{error}</p>
          ) : (
            <ul className="ua-users">
              {visible.map((a) => (
                <li key={a.id}>
                  <button
                    className={`ua-user ${a.id === selectedId ? 'active' : ''}`}
                    onClick={() => startEdit(a)}
                  >
                    <span className="ua-user-name">{a.title || '(untitled)'}</span>
                    <span className="ua-user-sub">{shortDate(a.date)}</span>
                    {a.archived && (
                      <span className="ua-tags">
                        <span className="ua-tag ua-tag-disabled">Archived</span>
                      </span>
                    )}
                  </button>
                </li>
              ))}
              {!visible.length && (
                <li className="ua-muted">
                  {articles.length ? 'No matching articles.' : 'No articles yet — create one.'}
                </li>
              )}
            </ul>
          )}
        </aside>

        {/* Right: editor */}
        <section className="ua-detail glass">
          {!selectedId ? (
            <div className="ua-empty">
              <p>Select an article to edit, or create a new one.</p>
            </div>
          ) : (
            <>
              <div className="na-editor-head">
                <h2>{selectedId === 'new' ? 'New article' : 'Edit article'}</h2>
                <button className="admin-link" onClick={closeEditor}>Close</button>
              </div>

              <div className="na-form">
                <label className="na-field">
                  <span className="na-label">Title</span>
                  <input
                    className="na-input"
                    type="text"
                    value={form.title}
                    onChange={setField('title')}
                    placeholder="Article title"
                    maxLength={200}
                  />
                </label>

                <label className="na-field na-field-date">
                  <span className="na-label">Date</span>
                  <input
                    className="na-input"
                    type="date"
                    value={form.date}
                    onChange={setField('date')}
                  />
                </label>

                <label className="na-field">
                  <span className="na-label">
                    Content <span className="na-hint">(Markdown supported)</span>
                  </span>
                  <textarea
                    className="na-textarea"
                    value={form.content}
                    onChange={setField('content')}
                    placeholder="Write the article in Markdown…"
                    rows={14}
                  />
                </label>

                <div className="na-actions">
                  <button className="btn-primary" onClick={save} disabled={!canSave}>
                    {saving ? 'Saving…' : selectedId === 'new' ? 'Create article' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    className="na-preview-toggle"
                    onClick={() => setShowPreview((v) => !v)}
                  >
                    {showPreview ? 'Hide preview' : 'Show preview'}
                  </button>
                  {selected && (
                    selected.archived ? (
                      <button
                        type="button"
                        className="ua-reenable na-archive"
                        onClick={() => toggleArchived(selected, false)}
                        disabled={archiving}
                      >
                        {archiving ? 'Restoring…' : 'Restore'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="ua-disable na-archive"
                        onClick={() => toggleArchived(selected, true)}
                        disabled={archiving}
                      >
                        {archiving ? 'Archiving…' : 'Archive'}
                      </button>
                    )
                  )}
                </div>

                {error && <p className="admin-error">{error}</p>}

                {selected && (
                  <p className="na-meta">
                    {selected.archived ? 'Archived — hidden from members. ' : 'Published. '}
                    {selected.updatedBy && `Last edited by ${selected.updatedBy}.`}
                  </p>
                )}
              </div>

              {showPreview && (
                <div className="na-preview">
                  <span className="na-preview-title">Preview</span>
                  <NewsArticle
                    article={{
                      id: 'preview',
                      title: form.title || '(untitled)',
                      date: form.date,
                      content: form.content,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default NewsAdmin;
